/**
 * Custom Validators
 * Các validator tùy chỉnh cho base form system
 */

import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { VALIDATION_PATTERNS } from '../constants/validation-constants';

/**
 * Validator cho số điện thoại VN
 */
export function mobilephoneValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        if (!control.value) {
            return null;
        }

        const isValid = VALIDATION_PATTERNS.mobilephone.test(control.value);
        return isValid ? null : { mobilephone: { valid: false } };
    };
}

/**
 * Validator cho URL
 */
export function urlValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        if (!control.value) {
            return null;
        }

        const isValid = VALIDATION_PATTERNS.url.test(control.value);
        return isValid ? null : { url: { valid: false } };
    };
}

/**
 * Validator cho mã số thuế
 */
export function taxcodeValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        if (!control.value) {
            return null;
        }

        const isValid = VALIDATION_PATTERNS.taxcode.test(control.value);
        return isValid ? null : { taxcode: { valid: false } };
    };
}

/**
 * Validator cho CMND/CCCD
 */
export function licenseValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        if (!control.value) {
            return null;
        }

        const isValid = VALIDATION_PATTERNS.license.test(control.value);
        return isValid ? null : { license: { valid: false } };
    };
}

/**
 * Validator cho password mạnh
 */
export function passwordValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        if (!control.value) {
            return null;
        }

        const isValid = VALIDATION_PATTERNS.password.test(control.value);
        return isValid ? null : { password: { valid: false } };
    };
}

/**
 * Validator cho số thập phân
 */
export function decimalValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        if (!control.value) {
            return null;
        }

        const valueString = String(control.value);
        const valueWithoutCommas = valueString.replace(/,/g, '');
        const isValid = !Number.isNaN(Number(valueWithoutCommas));

        return isValid ? null : { decimal: { valid: false } };
    };
}

/**
 * Validator cho số dương (> 0)
 */
export function positiveValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        if (!control.value) {
            return null;
        }

        const valueString = String(control.value);
        const valueWithoutCommas = valueString.replace(/,/g, '');
        const numValue = Number(valueWithoutCommas);

        return numValue > 0 ? null : { positive: { valid: false } };
    };
}

/**
 * Validator cho số nguyên không âm
 */
export function integerValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        if (!control.value) {
            return null;
        }

        const numValue = Number(control.value);
        const isValid = numValue >= 0 && Number.isInteger(numValue);

        return isValid ? null : { integer: { valid: false } };
    };
}

/**
 * Conditional Required Validator
 * Required chỉ khi điều kiện = true
 */
export function requiredIf(condition: () => boolean): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        if (!condition()) {
            return null;
        }

        const value = control.value;
        if (value == null || value === undefined || value === '') {
            return { requiredIf: { condition: true } };
        }

        return null;
    };
}

/**
 * Compare Validator
 * So sánh với field khác (ví dụ: password confirm)
 */
export function compareWith(getOtherValue: () => any, errorKey: string = 'mismatch'): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        if (!control.value) {
            return null;
        }

        const otherValue = getOtherValue();
        const isMatch = control.value === otherValue;

        return isMatch ? null : { [errorKey]: { valid: false } };
    };
}

/**
 * Whitespace Validator
 * Không cho phép chỉ toàn khoảng trắng
 */
export function noWhitespaceValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        if (!control.value) {
            return null;
        }

        const isWhitespace = (control.value || '').trim().length === 0;
        return isWhitespace ? { whitespace: { valid: false } } : null;
    };
}

/**
 * Factory function để lấy validator theo preset type
 */
export function getPresetValidator(preset: string): ValidatorFn | null {
    const validators: { [key: string]: ValidatorFn } = {
        mobilephone: mobilephoneValidator(),
        url: urlValidator(),
        taxcode: taxcodeValidator(),
        license: licenseValidator(),
        password: passwordValidator(),
        decimal: decimalValidator(),
        positive: positiveValidator(),
        integer: integerValidator(),
    };

    return validators[preset] || null;
}
